// tabs/FilesTab.tsx
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Text, Divider, SimpleGrid, Card, Group, ActionIcon, Anchor, Stack, Tooltip, Alert } from '@mantine/core';
import {
  IconTrash, IconEye, IconFileTypePdf, IconFileTypeDoc, IconFileTypeXls,
  IconFileTypeZip, IconPhoto, IconFile, IconRefresh, IconAlertCircle,
} from '@tabler/icons-react';
import { useLeaderPortalService, CareCaseFile } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { FileUploader } from '@/components/ccms-components/file-management';
import { UniversalFileViewer } from '@/components/ccms-components/file-management/components/UniversalFileViewer';
import ConfirmActionModal from './ConfirmActionModal';

interface FilesTabProps {
  caseID: number;
  active: boolean;
  onCaseChange?: () => void;
}

interface CompletedUpload {
  url: string;
  fileName: string;
  mimeType?: string;
}

const DELETE_WINDOW_MS = 10 * 60 * 1000; // 10 minutes

const getFileIcon = (mimeType?: string | null) => {
  if (!mimeType) return IconFile;
  if (mimeType.startsWith('image/')) return IconPhoto;
  if (mimeType === 'application/pdf') return IconFileTypePdf;
  if (mimeType.includes('word') || mimeType.includes('document')) return IconFileTypeDoc;
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return IconFileTypeXls;
  if (mimeType.includes('zip') || mimeType.includes('compressed')) return IconFileTypeZip;
  return IconFile;
};

// SDK dates come as yyyyMMddHHmmss numbers/strings — parse to a real Date so
// we can diff against "now" for the 10-minute delete window.
function sdkDateToDate(value: number | string): Date | null {
  const raw = String(value ?? '');
  if (raw.length < 14) return null;
  const year   = Number(raw.slice(0, 4));
  const month  = Number(raw.slice(4, 6)) - 1;
  const day    = Number(raw.slice(6, 8));
  const hour   = Number(raw.slice(8, 10));
  const minute = Number(raw.slice(10, 12));
  const second = Number(raw.slice(12, 14));
  return new Date(year, month, day, hour, minute, second);
}

function canDeleteFile(file: CareCaseFile): boolean {
  const uploaded = sdkDateToDate(file.uploadedDateTime);
  if (!uploaded) return false;
  return Date.now() - uploaded.getTime() <= DELETE_WINDOW_MS;
}

export default function FilesTab({ caseID, active, onCaseChange }: FilesTabProps) {
  const leaderPortal = useLeaderPortalService();

  const [files, setFiles]     = useState<CareCaseFile[]>([]);
  const [loading, setLoading] = useState(false);

  const [confirmOpen, setConfirmOpen]       = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction]   = useState<(() => Promise<void>) | null>(null);

  // ── Universal file viewer (preview) ────────────────────────────────────────
  const [viewerOpen, setViewerOpen]   = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const viewerFiles = useMemo(
    () => files.map((f) => ({
      id:       String(f.fileID),
      url:      f.url ?? undefined,
      fileName: f.name ?? `File ${f.fileID}`,
      mimeType: f.type ?? 'application/octet-stream',
      fileSize: (f as any).fileSize ?? (f as any).size ?? 0,
    })),
    [files]
  );

  const openPreview = (index: number) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };

  const fetchFiles = useCallback(async () => {
    if (!caseID) return;
    setLoading(true);
    try {
      const result = await leaderPortal.caseFiles(caseID);
      setFiles(result ?? []);
    } catch (e) {
      console.error('Error fetching files:', e);
      toast.error('Error', 'Could not load files.');
    } finally {
      setLoading(false);
    }
  }, [caseID, leaderPortal]);

  useEffect(() => { if (active) fetchFiles(); }, [active, fetchFiles]);

  const handleUploadComplete = async (uploaded: CompletedUpload[]) => {
    try {
      await Promise.all(
        uploaded.map((file) =>
          leaderPortal.addCaseFile(caseID, {
            name: file.fileName,
            url:  file.url,
            type: file.mimeType,
          })
        )
      );
      await fetchFiles();
      onCaseChange?.();
      toast.success(
        'File Added',
        `${uploaded.length} file${uploaded.length !== 1 ? 's' : ''} uploaded successfully.`
      );
    } catch (e) {
      console.error(e);
      toast.error('Error', 'File uploaded but failed to attach it to the case.');
    }
  };

  const handleRemoveFile = (file: CareCaseFile) => {
    if (!canDeleteFile(file)) {
      toast.error('Cannot Delete', 'This file can only be deleted within 10 minutes of upload.');
      return;
    }
    setConfirmMessage(`Remove this file${file.name ? ` (${file.name})` : ''} from the case?`);
    setConfirmAction(() => async () => {
      setLoading(true);
      try {
        await leaderPortal.deleteCaseFile(caseID, file.fileID);
        await fetchFiles();
        onCaseChange?.();
        toast.warning('File Removed', 'File removed from case.');
      } catch (e) {
        console.error(e);
        toast.error('Error', 'Failed to remove file.');
      } finally {
        setLoading(false);
      }
    });
    setConfirmOpen(true);
  };

  return (
    <Stack gap="md">
      <ConfirmActionModal
        opened={confirmOpen}
        title="Remove File"
        message={confirmMessage}
        onConfirm={async () => { if (confirmAction) await confirmAction(); }}
        onClose={() => { setConfirmOpen(false); setConfirmAction(null); }}
      />

      <UniversalFileViewer
        opened={viewerOpen}
        onClose={() => setViewerOpen(false)}
        files={viewerFiles}
        initialIndex={viewerIndex}
        onIndexChange={setViewerIndex}
      />

      <Alert
        variant="light"
        color="yellow"
        title="Notice"
        icon={<IconAlertCircle size={18} />}
      >
        Files can only be deleted within 10 minutes of upload.
      </Alert>

        <Group justify="right" >
        <Tooltip label="Refresh files" withArrow>
          <ActionIcon
            variant="subtle"
            onClick={fetchFiles}
            loading={loading}
            size="lg"
          >
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <FileUploader
        domain="CareCase"
        recordId={String(caseID)}
        multiple
        accept={['*/*']}
        onUploadComplete={handleUploadComplete}
        onUploadError={(err: any) => {
          console.error(err);
          toast.error('Upload Failed', err?.message ?? 'Could not upload file.');
        }}
      />

      <Divider my="md" />

      {files.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center">No files uploaded yet.</Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {files.map((f, index) => {
            const FileIcon = getFileIcon(f.type);
            const deletable = canDeleteFile(f);
            return (
              <Card key={f.fileID} withBorder radius="md" padding="lg">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Group gap="sm" wrap="nowrap" style={{ overflow: 'hidden' }}>
                    <FileIcon size={28} />
                    <div style={{ overflow: 'hidden' }}>
                      <Text fw={700} truncate>{f.name ?? `File ${f.fileID}`}</Text>
                      {f.url && (
                        <Anchor component="button" type="button" onClick={() => openPreview(index)} size="xs">
                          Preview
                        </Anchor>
                      )}
                      <Text size="xs" c="dimmed">
                        Uploaded: {f.uploadedDateTime ? String(f.uploadedDateTime) : 'Unknown'}
                      </Text>
                    </div>
                  </Group>
                  <Group gap={4} wrap="nowrap">
                    {f.url && (
                      <Tooltip label="Preview">
                        <ActionIcon variant="light" color="blue" onClick={() => openPreview(index)}>
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    <Tooltip label={deletable ? 'Remove file' : 'Delete window (10 min) has passed'}>
                      <ActionIcon
                        variant="light"
                        color="red"
                        disabled={!deletable}
                        onClick={() => handleRemoveFile(f)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Group>
              </Card>
            );
          })}
        </SimpleGrid>
      )}
    </Stack>
  );
}